import type { ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay/index";
import React from "react";
import ChargeRefundsViewModel from "../../../../../viewModels/callCenter/search/charges/chargeDetail/chargeRefundsViewModel";
import { registerView } from "@frui.ts/views/dist";
import { refundDisplayStatusVariant } from "../../../../../entities/refundDisplayStatus";
import { formatAmount, formatDate } from "../../../../../utils/localizations";
import { formatBankAccount } from "@edaz/utils/bankAccount";
import { emptyValue } from "utils/emptyValue";

const ChargeRefundsView: ViewComponent<ChargeRefundsViewModel> = observer(({ vm }) => {
  if (!vm.charge) {
    return null;
  }

  const valueOrDash = (value?: string | number): string => {
    return value ? String(value) : emptyValue;
  };

  return (
    <>
      <LoaderOverlay loading={vm.busyWatcher.isBusy}>
        <table className="table table-full table-tbody-striped">
          <thead>
            <tr>
              <th style={{ minWidth: 150 }}>{vm.translate("refundDisplayStatus")}</th>
              <th style={{ minWidth: 140 }}>{vm.translate("refundDisplayType")}</th>
              <th style={{ minWidth: 140 }}>{vm.translate("officialRecordId")}</th>
              <th style={{ minWidth: 200 }}>{vm.translate("originalChargePrice")}</th>
              <th style={{ minWidth: 170 }}>{vm.translate("amount")}</th>
              <th style={{ minWidth: 110 }}>{vm.translate("fees")}</th>
              <th style={{ minWidth: 160 }}>{vm.translate("createdAt")}</th>
              <th style={{ minWidth: 150 }}>{vm.translate("createdBy")}</th>
              <th style={{ minWidth: 250 }}>{vm.translate("approvedOrRejectedAt")}</th>
              <th style={{ minWidth: 210 }}>{vm.translate("approvedOrRejectedBy")}</th>
              <th style={{ minWidth: 160 }}>{vm.translate("exportedAt")}</th>
              <th style={{ minWidth: 180 }}>{vm.translate("customerRefundPaymentType")}</th>
              <th style={{ minWidth: 180 }}>{vm.translate("refundTransferPaymentBankAccount")}</th>
              <th style={{ minWidth: 180 }}>{vm.translate("refundTransferPaymentCardNumber")}</th>
              <th style={{ minWidth: 180 }}>{vm.translate("variableSymbol")}</th>
              <th style={{ minWidth: 200 }}>{vm.translate("paymentDate")}</th>
            </tr>
          </thead>
          <>
            {vm.items.map(item => {
              return (
                <tbody key={`${item.variableSymbol}`}>
                  <tr>
                    <td>
                      <strong className={`text-${refundDisplayStatusVariant(item.refundDisplayStatus)}`}>
                        {vm.translateChargeRefundDisplayStatus(item.refundDisplayStatus)}
                      </strong>
                    </td>
                    <td>{vm.translateChargeRefundDisplayType(item.refundDisplayType)}</td>
                    <td className="text-nowrap">{valueOrDash(item.officialRecordId)}</td>
                    <td>{formatAmount(item.originalChargePrice)}</td>
                    <td>{formatAmount(item.amount)}</td>
                    <td>{formatAmount(item.fees.map(f => f.amount).reduce((a, b) => a + b, 0))}</td>
                    <td>{formatDate(item.createdAt)}</td>
                    <td>{item.createdBy}</td>
                    <td>{valueOrDash(formatDate(vm.approvedOrRejectedAt(item)))}</td>
                    <td>{valueOrDash(vm.approvedOrRejectedBy(item))}</td>
                    <td>{valueOrDash(formatDate(item.exportedAt))}</td>
                    <td>{valueOrDash(vm.refundPaymentType(item.customerRefundPaymentType))}</td>
                    <td>{valueOrDash(formatBankAccount(item.refundTransferPayment?.counterpartyBank?.bankAccount))}</td>
                    <td>{valueOrDash(item.refundPayGatePayment?.cardNumber)}</td>
                    <td>{valueOrDash(item.variableSymbol)}</td>
                    <td>{valueOrDash(formatDate(vm.refundPaymentDate(item)))}</td>
                  </tr>
                </tbody>
              );
            })}
          </>
        </table>
        {vm.items?.length === 0 && (
          <div className="bg-white mt-n2 mb-4 px-4 py-3 border-bottom text-muted full-width">
            {vm.translateGeneral(vm.busyWatcher.isBusy ? "loading" : "empty")}
          </div>
        )}
      </LoaderOverlay>
    </>
  );
});

registerView(ChargeRefundsView, ChargeRefundsViewModel);
